<template>
  <main
    v-if="isReady"
    id="app-main"
    data-testid="main"
    :class="$style.main"
  >
    <router-view :data-testid="`${routeName}-view`" />

    <receipt-modal data-testid="receipt-modal" />

    <common-modal data-testid="common-modal" />

    <otp-modal data-testid="otp-modal" />

    <promo-modals
      v-if="isUnifiedPromoModalsVisible"
      data-testid="promo-modals"
    />

    <mobile-app-installed
      v-if="showMobileAppInstalled"
      data-testid="mobile-app-installed"
    />

    <b-loader
      v-if="loading"
      class="position-fixed"
    />

    <offline-toast data-testid="offline-toast" />

    <hints-tour data-testid="hints-tour" />
    <hints-modal data-testid="hints-modal" />
  </main>
  <connection-problem
    v-else-if="connectionProblem"
    :problem="connectionProblem"
    data-testid="connection-problem"
  />

  <maintenance-mode
    v-else-if="isMaintenanceMode"
    data-testid="maintenance-mode"
  />

  <loading-screen v-else />
</template>
<script>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  defineAsyncComponent,
} from 'vue'
import { mapState, mapGetters } from 'vuex'
import { datadogRum } from '@datadog/browser-rum'
import { until } from '@vueuse/core'

import { useDeviceDetect } from '@/composables/device-detect/index.js'
import { useAppVisibility } from '@/composables/app-visibility/index.ts'
import {
  createWindowResizeListener,
  destroyWindowResizeListener,
} from '@/composables/resize-listener/index.js'
import { useReady } from '@/composables/ready/index.js'
import { useCore } from '@/composables/core/index.js'
import { useDevConsole } from '@/composables/dev-console/index.js'
import { useEppo, whenEppoIsReady } from '@/composables/eppo/index.js'
import { useWebViewEventBus } from '@/composables/web-view-event-bus/index.js'
import { useEmitter } from '@/composables/emitter/index.js'
import { useHeader } from '@/composables/header/index.js'
import { useMaintenanceMode } from './composables/maintenance-mode/index.js'

import { bootstrap } from '@/bootstrap.js'
import { EVENTS, FEATURE_FLAGS, DATADOG_CUSTOM_ACTIONS_NAMES } from '@/lib/consts.js'

export default {
  name: 'App',

  setup () {
    const { store, router } = useCore()
    const { touchable } = useDeviceDetect()
    const { isReady } = useReady()
    const webViewEventBus = useWebViewEventBus()
    const emitter = useEmitter()
    const connectionProblem = computed(
      () => store.state.routingConfig.connectionProblem
    )
    const isUnifiedPromoModalsVisible = ref(false)
    const mobileAppInstalled = computed(
      () => store.state.app.mobileAppInstalled
    )
    const isConsentForMobile = computed(() =>
      router.currentRoute.value.name?.includes('consent-mobile')
    )
    const routeName = computed(() => store.getters['app/currentRoute']?.name)

    useHeader().initialize()

    emitter.on(EVENTS.BUSINESS_STATE_CHANGE, () => {
      isUnifiedPromoModalsVisible.value = !!store.state.business.business
    })

    onMounted(() => {
      createWindowResizeListener()
    })

    onBeforeUnmount(() => {
      destroyWindowResizeListener()
    })

    const { isMaintenanceMode } = useMaintenanceMode()

    until(() => Boolean(isReady.value || isMaintenanceMode.value)).toBe(true)
      .then(() => {
        try {
          datadogRum.addAction(DATADOG_CUSTOM_ACTIONS_NAMES.STARTUP_SUCCESS)
        } catch (e) { /* do nothing */ }
      })

    return {
      touchable,
      isReady,
      connectionProblem,
      isUnifiedPromoModalsVisible,
      mobileAppInstalled,
      isConsentForMobile,
      routeName,
      webViewEventBus,
      isMaintenanceMode,
    }
  },

  data () {
    return {
      continuedWithBrowser: false,
    }
  },

  components: {
    ReceiptModal: defineAsyncComponent(() =>
      import('@/components/app/modals/receipt.vue')
    ),
    OtpModal: defineAsyncComponent(() =>
      import('@/components/app/modals/otp-modals/otp-modal.vue')
    ),
    CommonModal: defineAsyncComponent(() =>
      import('@/components/app/modals/common.vue')
    ),
    MobileAppInstalled: defineAsyncComponent(() =>
      import('@/views/mobile-app-installed/index.vue')
    ),
    ConnectionProblem: defineAsyncComponent(() =>
      import('@/views/connection-problem/index.vue')
    ),
    LoadingScreen: defineAsyncComponent(() =>
      import('@/views/loading-screen/index.vue')
    ),
    OfflineToast: defineAsyncComponent(() =>
      import('@/components/offline-toast/index.vue')
    ),
    HintsTour: defineAsyncComponent(() =>
      import('@/components/walkthroughs/components/hints-tour.vue')
    ),
    HintsModal: defineAsyncComponent(() =>
      import('@/components/walkthroughs/components/hints-modal.vue')
    ),
    PromoModals: defineAsyncComponent(() =>
      import('@/components/common/promo-modals.js')
    ),
    MaintenanceMode: defineAsyncComponent(() =>
      import('@/views/maintenance-mode/index.vue')
    ),
  },

  computed: {
    ...mapState({
      mode: (state) => state.app.mode,
      loading: (state) => state.loadingScreen.count > 0,
      webView: (state) => state.app.webView,
    }),

    ...mapGetters({
      isIOS: 'app/isIOS',
    }),

    showMobileAppInstalled () {
      return this.isIOS && this.mobileAppInstalled
    },
  },

  watch: {
    $route: {
      handler (to, from) {
        useDevConsole().logRouteChange(to, from)
      },
    },
  },

  async created () {
    await bootstrap({ context: this.$root })

    if (this.mode !== 'development' && this.webView) {
      document.addEventListener('contextmenu', this.disableContextmenu)
    }

    const { initAppVisibility } = useAppVisibility()
    initAppVisibility()
  },

  beforeUnmount () {
    if (this.mode !== 'development' && this.webView) {
      document.removeEventListener('contextmenu', this.disableContextmenu)
    }

    const { destroyAppVisibility } = useAppVisibility()
    destroyAppVisibility()

    this.webViewEventBus.destroy()
  },

  methods: {
    /*
     * we have to disable context menu, if we don't
     * there will be no way to open dev-console on
     * Android - long press opens contextual menu
     * like right mouse button on desktop
     */
    disableContextmenu (e) {
      e.preventDefault()
      e.stopPropagation()
      return false
    },
  },
}
</script>

<style lang="scss" module>
.main {
  display: flex;
  flex-direction: column;
  height: 100%;
  @include media-breakpoint-down($breakpoint-mobile) {
    min-height: 100%;
  }
}
.loader {
  position: fixed;
}
</style>

<style lang="scss">
// styles for appsflyer smartbanner to work with main template
body:has(.responsive-wrapper) {
  #app-main {
    height: calc(100% - #{$smartbanner-height});
    @include media-breakpoint-down($breakpoint-mobile) {
      min-height: calc(100% - #{$smartbanner-height});
    }
  }
}
</style>
